import { useEffect } from "react";

type SessionResumeGuardOptions = {
  /**
   * Re-checks whether the session is still valid. MUST resolve to `false` only
   * when the user is definitively logged out. Transient/network failures SHOULD
   * resolve to `true` (fail-open) so we never log out a still-authenticated user.
   */
  verify: () => Promise<boolean>;
  /**
   * Invoked when {@link verify} reports the session is no longer valid.
   * Responsible for clearing any in-memory token and redirecting.
   */
  onInvalid: () => void;
};

/**
 * Guards against back/forward-cache (bfcache) restores reviving a stale
 * in-memory session after logout.
 *
 * When a user logs out and presses the browser Back button, the browser can
 * restore the previous page straight from bfcache: the whole JS heap + DOM is
 * revived with no reload, no module re-init, and no re-check of the auth state.
 * The module-scoped access token therefore survives and the app keeps working
 * until the JWT expires. This hook re-verifies the session on bfcache restore
 * and, if it is gone, tears it down.
 *
 * Scope is deliberately limited to `pageshow` with `event.persisted === true`
 * (i.e. a genuine bfcache restore). We intentionally do NOT hook
 * `visibilitychange`/focus: those fire on every tab switch, including when a
 * user leaves the tab mid-payment to fetch an OTP, and a transient verify
 * result there would wrongly abort an in-progress transaction. Cross-tab logout
 * detection, if ever needed, should use a passive `storage` event on the Auth0
 * cache key rather than an active auth call on focus.
 */
export const useSessionResumeGuard = ({
  verify,
  onInvalid,
}: SessionResumeGuardOptions) => {
  useEffect(() => {
    let cancelled = false;
    let inFlight = false;

    const handlePageShow = async (event: PageTransitionEvent) => {
      // Only a bfcache restore revives stale in-memory state. A normal load
      // (persisted === false) re-runs module init and the usual auth checks.
      if (!event.persisted || inFlight) return;

      inFlight = true;
      try {
        const isValid = await verify();
        if (!cancelled && !isValid) {
          onInvalid();
        }
      } finally {
        inFlight = false;
      }
    };

    window.addEventListener("pageshow", handlePageShow);

    return () => {
      cancelled = true;
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, [verify, onInvalid]);
};
